
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, UserPlus, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "react-router-dom";

type UserWithRole = {
  id: string;
  email: string;
  full_name?: string;
  role: string;
  base_assignments: string[];
};

type AvailableBase = {
  id: number;
  name: string;
};

export const BaseAssignments = () => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [bases, setBases] = useState<AvailableBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedBase, setSelectedBase] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchSalesAssistants = async () => {
    try {
      setLoading(true);
      
      // Get all users with sales_assistant role
      const { data: salesAssistants, error: roleError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'sales_assistant');
      
      if (roleError) throw roleError;
      
      if (!salesAssistants?.length) {
        setUsers([]);
        return;
      }
      
      // Get user details for each sales assistant
      const userIds = salesAssistants.map(sa => sa.user_id);
      
      const { data: userProfiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', userIds);
        
      if (profileError) throw profileError;
      
      // Get role-based assignments
      const { data: roleBases, error: roleBasesError } = await supabase
        .from('role_base_assignments')
        .select('base_name')
        .eq('role', 'sales_assistant');
        
      if (roleBasesError) {
        console.error("Error fetching role-based assignments:", roleBasesError);
      }
      
      const roleBasesNames = roleBases ? roleBases.map(rb => rb.base_name) : [];
      
      // Get base assignments for each user (direct assignments)
      const usersWithBases = await Promise.all(
        (userProfiles || []).map(async (profile) => {
          const { data: baseAssignments, error: baseError } = await supabase
            .from('user_base_assignments')
            .select('base_name')
            .eq('user_id', profile.id);
            
          if (baseError) {
            console.error(`Error fetching bases for user ${profile.id}:`, baseError);
            return {
              ...profile,
              role: 'sales_assistant',
              base_assignments: roleBasesNames // Use role-based assignments
            };
          }
          
          // Merge role-based and direct assignments
          const directAssignments = baseAssignments ? baseAssignments.map(ba => ba.base_name) : [];
          const allAssignments = [...new Set([...directAssignments, ...roleBasesNames])];
          
          return {
            ...profile,
            role: 'sales_assistant',
            base_assignments: allAssignments
          };
        })
      );
      
      setUsers(usersWithBases);
    } catch (error: any) {
      console.error("Error fetching sales assistants:", error);
      toast.error("Failed to load sales assistants: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBases = async () => {
    try {
      const { data, error } = await supabase
        .from('bases')
        .select('id, name')
        .order('name');
        
      if (error) throw error;
      
      setBases(data || []);
    } catch (error: any) {
      console.error("Error fetching bases:", error);
      toast.error("Failed to load bases: " + error.message);
    }
  };

  const assignBaseToUser = async () => {
    if (!selectedUser || !selectedBase) {
      toast.error("Please select both a user and a base");
      return;
    }
    
    try {
      // Check if this assignment already exists
      const { data: existing, error: checkError } = await supabase
        .from('user_base_assignments')
        .select('id')
        .eq('user_id', selectedUser)
        .eq('base_name', selectedBase)
        .single();
        
      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }
      
      if (existing) {
        toast.info("This base is already assigned to the user");
        return;
      }
      
      // Create new assignment
      const { error } = await supabase
        .from('user_base_assignments')
        .insert([
          { user_id: selectedUser, base_name: selectedBase }
        ]);
        
      if (error) throw error;
      
      toast.success(`Base "${selectedBase}" assigned to user successfully`);
      setDialogOpen(false);
      fetchSalesAssistants(); // Refresh the list
    } catch (error: any) {
      console.error("Error assigning base:", error);
      toast.error("Failed to assign base: " + error.message);
    }
  };

  const removeBaseAssignment = async (userId: string, baseName: string) => {
    try {
      const { error } = await supabase
        .from('user_base_assignments')
        .delete()
        .eq('user_id', userId)
        .eq('base_name', baseName);
        
      if (error) throw error;
      
      toast.success(`Base "${baseName}" removed from user`);
      fetchSalesAssistants(); // Refresh the list
    } catch (error: any) {
      console.error("Error removing base assignment:", error);
      toast.error("Failed to remove base assignment: " + error.message);
    }
  };

  useEffect(() => {
    fetchSalesAssistants();
    fetchBases();
  }, []);

  // Get all role-based assignments for sales assistants
  const [roleBasedAssignments, setRoleBasedAssignments] = useState<string[]>([]);
  
  useEffect(() => {
    const fetchRoleBasedAssignments = async () => {
      try {
        const { data, error } = await supabase
          .from('role_base_assignments')
          .select('base_name')
          .eq('role', 'sales_assistant');
          
        if (error) throw error;
        
        setRoleBasedAssignments(data ? data.map(item => item.base_name) : []);
      } catch (error) {
        console.error("Error fetching role-based assignments:", error);
      }
    };
    
    fetchRoleBasedAssignments();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sales Assistant Base Assignments</CardTitle>
        <CardDescription>
          Manage which bases are accessible to sales assistants
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Base Access Management</AlertTitle>
          <AlertDescription>
            Sales assistants get base access through their role. You can configure these in the{" "}
            <Link to="/admin/roles" className="font-medium underline">
              Role Manager
            </Link>
            . Individual assignments below override the role-based assignments.
          </AlertDescription>
        </Alert>

        {roleBasedAssignments.length > 0 && (
          <div className="mb-6 p-4 border rounded-lg">
            <h3 className="text-sm font-medium mb-2">
              Role-based base assignments for all sales assistants:
            </h3>
            <div className="flex flex-wrap gap-2">
              {roleBasedAssignments.map((base) => (
                <Badge key={base} variant="outline">
                  {base}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-gray-500">No sales assistants found</p>
            <p className="text-sm text-gray-400 mt-1">
              Assign the sales_assistant role to users in the User Management section first
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {users.map((user) => (
              <div key={user.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-medium">{user.full_name || 'Unnamed User'}</h3>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                  <Dialog open={dialogOpen && selectedUser === user.id} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) setSelectedUser(null);
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedUser(user.id)}
                      >
                        Assign Base
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Assign Base to User</DialogTitle>
                        <DialogDescription>
                          Select a base to assign to {user.full_name || user.email}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="base">Base Name</Label>
                          <Select value={selectedBase} onValueChange={setSelectedBase}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a base" />
                            </SelectTrigger>
                            <SelectContent>
                              {bases.map((base) => (
                                <SelectItem key={base.id} value={base.name}>
                                  {base.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button 
                          variant="outline" 
                          onClick={() => setDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button onClick={assignBaseToUser}>Assign</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="mt-2">
                  <h4 className="text-sm font-medium mb-2">Assigned Bases:</h4>
                  {user.base_assignments.length === 0 ? (
                    <p className="text-sm text-gray-500">No bases assigned</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {user.base_assignments.map((base) => {
                        const isRoleBased = roleBasedAssignments.includes(base);
                        return (
                          <Badge 
                            key={base} 
                            variant={isRoleBased ? "outline" : "secondary"}
                            className="flex items-center gap-1"
                          >
                            {base}
                            {!isRoleBased && (
                              <button 
                                className="ml-1 h-4 w-4 rounded-full bg-gray-400/30 text-gray-500 hover:bg-gray-400/50 flex items-center justify-center text-xs"
                                onClick={() => removeBaseAssignment(user.id, base)}
                              >
                                ×
                              </button>
                            )}
                            {isRoleBased && (
                              <span className="ml-1 text-xs text-gray-500">(role)</span>
                            )}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <p className="text-sm text-muted-foreground">
          {users.length} sales assistant{users.length !== 1 ? 's' : ''} found
        </p>
        <Link to="/admin/roles">
          <Button variant="outline" size="sm">
            Manage Role Assignments
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};
